import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import api from '../api';

const MESES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MESES_FULL = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function formatCLP(val) {
  if (!val) return '$0';
  return '$' + val.toLocaleString('es-CL');
}

export default function DashboardEmpresaContabilidad({ empresa, anio, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMes, setSelectedMes] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, [empresa._id, anio]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/contabilidad/logs', { 
        params: { clienteId: empresa._id, anio } 
      });
      setLogs(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // Construir datos para gráficos anuales
  const dataAnual = MESES.slice(1).map((nombreMes, index) => {
    const mesNum = index + 1;
    // Filtrar logs del mes
    const logCompras = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'compras');
    const logVentas = logs.find(l => l.mes === mesNum && l.tipoDocumento === 'ventas');

    let totalCompras = 0;
    let totalVentas = 0;
    let docsCompras = 0;
    let docsVentas = 0;

    if (logCompras?.resumenSii) {
      logCompras.resumenSii.forEach(r => {
        totalCompras += parseInt(r.total || r.monto || 0, 10);
        docsCompras += parseInt(r.cantidadDocumentos || r.cantidad || 0, 10);
      });
    }

    if (logVentas?.resumenSii) {
      logVentas.resumenSii.forEach(r => {
        totalVentas += parseInt(r.total || r.monto || 0, 10);
        docsVentas += parseInt(r.cantidadDocumentos || r.cantidad || 0, 10);
      });
    }

    return {
      mes: nombreMes,
      mesNum,
      ventas: totalVentas,
      compras: totalCompras,
      docsVentas,
      docsCompras,
      okCompras: logCompras?.estado === 'completado',
      okVentas: logVentas?.estado === 'completado'
    };
  });

  const logsSeleccionados = selectedMes ? logs.filter(l => l.mes === selectedMes) : [];

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center justify-between">
            <div>
              <span>Dashboard Contable: {empresa.nombre}</span>
              <span className="text-sm font-normal text-gray-500 ml-3">RUT: {empresa.rut}</span>
            </div>
            <div className="text-lg text-blue-600 bg-blue-50 px-3 py-1 rounded-full mr-6">
              Año {anio}
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-20 text-center text-gray-500">Cargando información del año...</div>
        ) : (
          <div className="space-y-8 mt-4">
            
            {/* Gráficos Anuales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-xl p-4 bg-white shadow-sm">
                <h3 className="text-md font-semibold mb-4 text-gray-700">Evolución Monetaria (CLP)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dataAnual} margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mes" fontSize={12} />
                      <YAxis tickFormatter={(val) => '$' + (val/1000000).toFixed(1) + 'M'} fontSize={12} />
                      <Tooltip formatter={(val) => formatCLP(val)} />
                      <Legend />
                      <Bar dataKey="ventas" name="Ventas" fill="#3b82f6" radius={[4,4,0,0]} />
                      <Bar dataKey="compras" name="Compras" fill="#10b981" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="border rounded-xl p-4 bg-white shadow-sm">
                <h3 className="text-md font-semibold mb-4 text-gray-700">Volumen de Documentos</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dataAnual} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mes" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="docsVentas" name="Docs Ventas" stroke="#3b82f6" strokeWidth={2} />
                      <Line type="monotone" dataKey="docsCompras" name="Docs Compras" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Selector de Mes para Detalles */}
            <div className="border-t pt-6">
              <div className="flex items-center gap-4 mb-4">
                <h3 className="text-lg font-medium text-gray-800">Detalle Mensual (Drill-down)</h3>
                <Select value={selectedMes?.toString() || ''} onValueChange={(v) => setSelectedMes(parseInt(v))}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Seleccione un mes..." />
                  </SelectTrigger>
                  <SelectContent>
                    {dataAnual.map(d => (
                      <SelectItem key={d.mesNum} value={d.mesNum.toString()}>
                        {MESES_FULL[d.mesNum]} {(d.okCompras || d.okVentas) && '✓'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedMes && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {['ventas', 'compras'].map(tipo => {
                    const logMes = logsSeleccionados.find(l => l.tipoDocumento === tipo);
                    if (!logMes) return (
                      <div key={tipo} className="border rounded-xl p-6 text-center text-gray-500 bg-gray-50">
                        No hay registro de pipeline para {tipo} en {MESES_FULL[selectedMes]}.
                      </div>
                    );

                    return (
                      <div key={tipo} className="border rounded-xl overflow-hidden shadow-sm flex flex-col">
                        <div className={`px-4 py-3 border-b text-white font-medium capitalize flex justify-between items-center ${tipo === 'ventas' ? 'bg-blue-600' : 'bg-green-600'}`}>
                          <span>{tipo}</span>
                          <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full">{logMes.estado}</span>
                        </div>
                        
                        <div className="p-4 flex-1 space-y-4 text-sm bg-white">
                          {/* Archivos */}
                          <div>
                            <h4 className="font-semibold text-gray-700 mb-2 border-b pb-1">Archivos</h4>
                            <div className="flex flex-wrap gap-2">
                              {logMes.descargaSii?.archivo ? (
                                <a href={`/api/contabilidad/logs/${logMes._id}/archivos/${logMes.descargaSii.archivo.split(/[/\\]/).pop()}`} target="_blank" rel="noreferrer" 
                                   className="inline-flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-lg border">
                                  📄 Original SII
                                </a>
                              ) : <span className="text-gray-400 italic px-2">Sin archivo SII</span>}
                              
                              {logMes.procesamientoCsv?.archivo ? (
                                <a href={`/api/contabilidad/logs/${logMes._id}/archivos/${logMes.procesamientoCsv.archivo.split(/[/\\]/).pop()}`} target="_blank" rel="noreferrer" 
                                   className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200">
                                  📊 Procesado Inputsoft
                                </a>
                              ) : null}
                            </div>
                          </div>

                          {/* Resumen numérico */}
                          {logMes.resumenSii && logMes.resumenSii.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-700 mb-2 border-b pb-1">Resumen SII</h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs border">
                                  <thead className="bg-gray-50 border-b">
                                    <tr>
                                      <th className="p-2">Documento</th>
                                      <th className="p-2 text-right">Cant</th>
                                      <th className="p-2 text-right">Monto</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {logMes.resumenSii.map((r, i) => (
                                      <tr key={i}>
                                        <td className="p-2 text-gray-700 truncate max-w-[200px]" title={r.tipoDocumento}>{r.tipoDocumento}</td>
                                        <td className="p-2 text-right">{r.cantidadDocumentos || r.cantidad}</td>
                                        <td className="p-2 text-right">{formatCLP(parseInt(r.total || r.monto || 0, 10))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Errores Inputsoft */}
                          {logMes.subidaInputsoft?.detallesError?.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-red-600 mb-2 border-b border-red-200 pb-1">Alertas Inputsoft ({logMes.subidaInputsoft.detallesError.length})</h4>
                              <div className="max-h-40 overflow-y-auto border border-red-200 rounded">
                                <table className="w-full text-left text-xs">
                                  <thead className="bg-red-50 text-red-700 sticky top-0">
                                    <tr>
                                      <th className="p-2">Folio</th>
                                      <th className="p-2">Motivo</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-red-100">
                                    {logMes.subidaInputsoft.detallesError.map((e, i) => (
                                      <tr key={i} className="hover:bg-red-50/50">
                                        <td className="p-2 font-mono">{e.folio}</td>
                                        <td className="p-2 text-red-600">{e.motivo}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Log Técnico */}
                          <div className="mt-4 pt-4 border-t">
                            <details className="group">
                              <summary className="cursor-pointer font-medium text-gray-600 hover:text-gray-900 text-sm flex items-center">
                                <span className="mr-2 transition-transform group-open:rotate-90">▶</span>
                                Ver Log de Ejecución
                              </summary>
                              <div className="mt-3 p-3 bg-gray-900 text-gray-300 rounded text-xs font-mono max-h-64 overflow-y-auto whitespace-pre-wrap leading-relaxed">
                                {logMes.logEntries?.join('\n') || 'No hay logs registrados'}
                              </div>
                            </details>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
